import type { HTMLAttributes, ReactNode } from 'react';
import { BiCamera, BiImage } from 'react-icons/bi';

import { Avatar } from '@/components/Avatar';

import s from './ProfileIdentity.module.scss';

type ProfileIdentityProps = {
  /** The whole display name: a person's first and last, or a business's name. */
  name: string;
  /** Portrait or logo. Falls back to the name's initials, then to a placeholder. */
  avatar?: null | string;
  /** Replaces the initials with this glyph when there is no picture — for a record whose
      name is not the portrait's to spell out. */
  avatarIcon?: ReactNode;
  /** A short line under the name — the session role on a profile, the job title
      on a CV, the tagline on a business. Rendered as a pill; omitted when there
      is nothing to say. */
  meta?: null | string;
  /** The quietest line of the three: an email, or an address. */
  subline?: null | string;
  /** A word about the record itself rather than about whom it is for — where it
      came from, say. Sits directly under the name, because it qualifies it. */
  tag?: ReactNode;
  /**
   * Opens a larger view of the picture. Only meaningful once there is one, so
   * with no picture the portrait falls through to `onChangePhoto`.
   */
  onViewPhoto?: () => void;
  /** Absent on a picture that isn't yours to change — no overlay, no action. */
  onChangePhoto?: () => void;
  /** Drops the "Change photo" button under the portrait. Pair it with no
      `onViewPhoto`, so the portrait itself stays the way to the picker. */
  hidePhotoAction?: boolean;
  /** Laid over the portrait — the upload spinner and drop highlight of `usePhotoUpload`. */
  photoOverlay?: ReactNode;
  /** Makes the portrait a drop target for a new photo (`usePhotoUpload`'s `dropProps`). */
  photoDropProps?: Pick<HTMLAttributes<HTMLElement>, 'onDragOver' | 'onDragLeave' | 'onDrop'>;
  /** Anything belonging under the identity: a privacy switch, a completeness
      meter, contact actions. */
  children?: ReactNode;
};

/** Initials come off the name's own words, so "Ada Lovelace" and "Blue Bay
    Marine" both read as two letters rather than one or three. */
const initialsOf = (name: string) => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return { firstName: words[0], lastName: words.length > 1 ? words.at(-1) : undefined };
};

/**
 * Who a record is for: the picture, the name, one line of standing, one line of
 * contact. Shared so every screen that opens on a record opens the same way — a
 * portrait that changed size or a name that changed weight between two of them
 * reads as two different products.
 */
export const ProfileIdentity = ({
  name,
  avatar,
  avatarIcon,
  meta,
  subline,
  tag,
  onViewPhoto,
  onChangePhoto,
  hidePhotoAction = false,
  photoOverlay,
  photoDropProps,
  children,
}: ProfileIdentityProps) => {
  const hasPhoto = !!avatar;
  /* With no photo there is nothing to preview, so the portrait falls through to
     the picker — and the label has to follow the action it actually runs, not the
     one the photo's presence suggests. */
  const viewing = hasPhoto && !!onViewPhoto;
  const portraitAction = viewing ? onViewPhoto : onChangePhoto;
  const portraitLabel = viewing ? 'View Photo' : hasPhoto ? 'Change Photo' : 'Add Photo';

  const portrait = (
    <Avatar
      src={avatar}
      alt={name}
      {...(avatarIcon ? {} : initialsOf(name))}
      icon={avatarIcon}
      size={96}
    />
  );

  return (
    <div className={s.identity}>
      <div className={s.portraitBox} {...photoDropProps}>
        {portraitAction ? (
          /* The whole portrait is a control, with its label revealed on hover or
           keyboard focus. */
          <button
            type="button"
            className={s.avatarButton}
            aria-label={portraitLabel}
            onClick={portraitAction}
          >
            {portrait}
            <span className={s.avatarOverlay} aria-hidden="true">
              {hasPhoto ? <BiImage /> : <BiCamera />}
              <span className={s.avatarOverlayText}>{portraitLabel}</span>
            </span>
          </button>
        ) : (
          portrait
        )}
        {photoOverlay}
      </div>

      {/* Stays visible at every width: once there is a photo the overlay opens
          the preview, so this is the only path to the picker. */}
      {onChangePhoto && !hidePhotoAction && (
        <button type="button" className={s.photoAction} onClick={onChangePhoto}>
          <BiCamera aria-hidden="true" />
          {hasPhoto ? 'Change Photo' : 'Add Photo'}
        </button>
      )}

      <div className={s.identityText}>
        <span className={s.identityName}>{name}</span>
        {tag}
        {/* What you are ranks above the address you are reachable at, so it sits
            between the name and the subline. */}
        {meta && <span className={s.identityMeta}>{meta}</span>}
        {subline && <span className={s.identitySubline}>{subline}</span>}
      </div>

      {children}
    </div>
  );
};
